import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../../components/Messages';
import { showApiError } from '../../utils/errorUtils';
import UOMSelect from '../../components/Form/UOMSelect';
import '../Admin/Users.css';
import { formatAppDate } from '../../utils/dateUtils';

const ViewRecipe = forwardRef(({
  recipeId,
  editMode = false,
  createMode = false,
  onBack,
  onEdit,
  onSave,
  onCancel,
  onRecipeCreated,
  onStatusLoaded,
}, ref) => {
  const { t } = useTranslation('production');
  const { t: tCommon } = useTranslation('common');
  const { showSuccess, showError } = useMessages();

  // Form state
  const [form, setForm] = useState({
    recipe_code: '',
    recipe_name: '',
    description: '',
    output_item_id: '',
    output_quantity: '1',
    output_uom: 'KG',
    status: 'DRAFT',
    output_item_code: '',
    output_item_name: '',
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Inventory items for selector
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Searchable select state with debounced server-side search
  const [outputSearchText, setOutputSearchText] = useState('');
  const [ingredientSearchTexts, setIngredientSearchTexts] = useState({});
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [debouncedItemSearch, setDebouncedItemSearch] = useState('');

  useImperativeHandle(ref, () => ({
    handleSave,
    handleDelete,
  }));

  // Debounce search input — wait 300ms after typing stops
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedItemSearch(itemSearchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [itemSearchTerm]);

  // Fetch inventory items when debounced search changes (server-side search)
  useEffect(() => {
    if (editMode || createMode) {
      fetchInventoryItems(debouncedItemSearch);
    }
  }, [debouncedItemSearch, editMode, createMode]);

  useEffect(() => {
    if (recipeId && !createMode) {
      fetchRecipe();
    }
  }, [recipeId, createMode]);

  const fetchRecipe = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(getApiUrl(`/api/formulations/recipes/${recipeId}`));
      if (res.ok) {
        const data = await res.json();
        setForm({
          recipe_code: data.recipe_code || '',
          recipe_name: data.recipe_name || '',
          description: data.description || '',
          output_item_id: data.output_item_id || '',
          output_quantity: data.output_quantity || '1',
          output_uom: data.output_uom || 'KG',
          status: data.status || 'DRAFT',
          output_item_code: data.output_item_code || '',
          output_item_name: data.output_item_name || '',
          created_by: data.created_by || null,
          created_date: data.created_date || null,
          updated_by: data.updated_by || null,
          updated_date: data.updated_date || null,
        });
        setItems(data.items || []);
        // Initialize search texts for existing data
        if (data.output_item_code) {
          setOutputSearchText(`${data.output_item_name} (${data.output_item_code})`);
        }
        const searchTexts = {};
        (data.items || []).forEach((item, idx) => {
          if (item.item_code) {
            searchTexts[idx] = `${item.item_name} (${item.item_code})`;
          }
        });
        setIngredientSearchTexts(searchTexts);
        if (onStatusLoaded) onStatusLoaded(data.status);
      }
    } catch (e) {
      console.error('Error fetching recipe:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryItems = async (search = '') => {
    setLoadingItems(true);
    try {
      // Inks, varnishes, solvents and formulated inks are all item_type INK
      // (enum RAW, INK, PACKAGING, CONSUMABLE, FINISHED_GOOD): there is no SOLVENT/FORMULATION type
      const params = new URLSearchParams({ page_size: '50', is_active: 'true', item_type: 'INK' });
      if (search) params.set('search', search);

      const res = await apiFetch(getApiUrl(`/api/inventory?${params}`));
      const data = res.ok ? await res.json() : { items: [] };
      setInventoryItems(data.items || data || []);
    } catch (e) {
      console.error('Error fetching inventory items:', e);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        output_item_id: parseInt(form.output_item_id),
        output_quantity: parseFloat(form.output_quantity),
        items: items.map((item, idx) => ({
          inventory_item_id: parseInt(item.inventory_item_id),
          quantity: parseFloat(item.quantity),
          unit_of_measure: item.unit_of_measure || 'KG',
          unit_cost: parseFloat(item.unit_cost || 0),
          sequence_order: idx + 1,
        })),
      };

      let res;
      if (createMode) {
        res = await apiFetch(getApiUrl('/api/formulations/recipes'), {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      } else {
        res = await apiFetch(getApiUrl(`/api/formulations/recipes/${recipeId}`), {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      }

      if (res.ok) {
        const data = await res.json();
        showSuccess(createMode ? t('viewRecipe.toasts.created') : t('viewRecipe.toasts.updated'));
        if (createMode && onRecipeCreated) {
          onRecipeCreated(data.recipe_id);
        } else if (onSave) {
          onSave();
        }
      } else {
        await showApiError(res, showError, tCommon);
        return;
      }
    } catch (e) {
      showError(t('viewRecipe.toasts.saveError'));
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('viewRecipe.confirm.delete'))) return;
    try {
      const res = await apiFetch(getApiUrl(`/api/formulations/recipes/${recipeId}`), {
        method: 'DELETE',
      });
      if (res.ok || res.status === 204) {
        showSuccess(t('viewRecipe.toasts.deleted'));
        if (onBack) onBack();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewRecipe.toasts.deleteError'));
    }
  };

  const handleRefreshCosts = async () => {
    try {
      const res = await apiFetch(getApiUrl(`/api/formulations/recipes/${recipeId}/refresh-costs`), {
        method: 'POST',
      });
      if (res.ok) {
        showSuccess(t('viewRecipe.toasts.costsRefreshed'));
        fetchRecipe();
      } else {
        await showApiError(res, showError, tCommon);
      }
    } catch (e) {
      showError(t('viewRecipe.toasts.refreshCostsError'));
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addItem = () => {
    setItems(prev => [...prev, {
      inventory_item_id: '',
      quantity: '',
      unit_of_measure: 'KG',
      unit_cost: '0',
      item_code: '',
      item_name: '',
    }]);
  };

  const updateItem = (idx, field, value) => {
    setItems(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };

      // Auto-fill item details when selecting material
      if (field === 'inventory_item_id' && value) {
        const invItem = inventoryItems.find(i => i.item_id === parseInt(value));
        if (invItem) {
          updated[idx].item_code = invItem.item_code;
          updated[idx].item_name = invItem.item_name;
          updated[idx].unit_of_measure = invItem.unit_of_measure || 'KG';
          updated[idx].unit_cost = invItem.average_cost || invItem.cost_per_unit || 0;
        }
      }
      return updated;
    });
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
    // Re-index ingredient search texts after removal
    setIngredientSearchTexts(prev => {
      const updated = {};
      Object.keys(prev).forEach(key => {
        const k = parseInt(key);
        if (k < idx) updated[k] = prev[k];
        else if (k > idx) updated[k - 1] = prev[k];
      });
      return updated;
    });
  };

  const calcExtendedCost = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const cost = parseFloat(item.unit_cost) || 0;
    return (qty * cost).toFixed(4);
  };

  const totalCost = items.reduce((sum, item) => sum + parseFloat(calcExtendedCost(item)), 0);

  const isEditing = editMode || createMode;

  if (loading) {
    return <div className="view-container"><div className="loading">{t('viewRecipe.loading')}</div></div>;
  }

  const formatCurrency = (val) => val != null ? `$${parseFloat(val).toFixed(2)}` : '-';

  return (
    <div className="view-container" style={{ padding: '16px' }}>
      <div className="view-header" style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>{createMode ? t('viewRecipe.newTitle') : form.recipe_name || t('viewRecipe.recipe')}</h2>
        {!isEditing && recipeId && (
          <div style={{ display: 'flex', gap: '6px' }}>
            <button className="btn-secondary" onClick={handleRefreshCosts} title={t('viewRecipe.refreshCostsHint')}>
              ↻ {t('viewRecipe.refreshCosts')}
            </button>
          </div>
        )}
      </div>

      {/* Header Fields */}
      <div className="form-card" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
        <div className="form-group">
          <label>{t('viewRecipe.fields.recipeCode')}</label>
          {isEditing ? (
            <input type="text" value={form.recipe_code} onChange={(e) => handleChange('recipe_code', e.target.value)} placeholder={t('viewRecipe.placeholders.recipeCode')} maxLength={100} />
          ) : (
            <div className="field-value"><strong>{form.recipe_code}</strong></div>
          )}
        </div>

        <div className="form-group">
          <label>{t('viewRecipe.fields.recipeName')}</label>
          {isEditing ? (
            <input type="text" value={form.recipe_name} onChange={(e) => handleChange('recipe_name', e.target.value)} placeholder={t('viewRecipe.placeholders.recipeName')} />
          ) : (
            <div className="field-value">{form.recipe_name}</div>
          )}
        </div>

        <div className="form-group">
          <label>{t('viewRecipe.fields.outputItem')}</label>
          {isEditing ? (
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                list="output-item-list"
                value={outputSearchText}
                onChange={(e) => {
                  const value = e.target.value;
                  setOutputSearchText(value);
                  setItemSearchTerm(value);
                  const match = inventoryItems.find(i =>
                    `${i.item_name} (${i.item_code})` === value ||
                    i.item_code === value ||
                    i.item_name === value
                  );
                  if (match) {
                    handleChange('output_item_id', match.item_id);
                  } else if (!value) {
                    handleChange('output_item_id', '');
                  }
                }}
                placeholder={t('viewRecipe.placeholders.searchItems')}
              />
              <datalist id="output-item-list">
                {inventoryItems.map(item => (
                  <option key={item.item_id} value={`${item.item_name} (${item.item_code})`} />
                ))}
              </datalist>
            </div>
          ) : (
            <div className="field-value">{form.output_item_code ? `${form.output_item_code} - ${form.output_item_name}` : '-'}</div>
          )}
        </div>

        <div className="form-group" style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: '8px' }}>
          <div>
            <label>{t('viewRecipe.fields.outputQuantity')}</label>
            {isEditing ? (
              <input type="number" step="0.0001" value={form.output_quantity} onChange={(e) => handleChange('output_quantity', e.target.value)} />
            ) : (
              <div className="field-value">{form.output_quantity}</div>
            )}
          </div>
          <div>
            <label>{t('tableHeaders.unitOfMeasure')}</label>
            {isEditing ? (
              <UOMSelect
                value={form.output_uom}
                onChange={(value) => handleChange('output_uom', value)}
              />
            ) : (
              <div className="field-value">{form.output_uom}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>{t('common:ui.status')}</label>
          {isEditing ? (
            <select value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
              <option value="DRAFT">{t('recipes.status.DRAFT')}</option>
              <option value="ACTIVE">{t('recipes.status.ACTIVE')}</option>
              <option value="OBSOLETE">{t('recipes.status.OBSOLETE')}</option>
            </select>
          ) : (
            <div className="field-value">
              <span className={`status-badge ${form.status === 'ACTIVE' ? 'status-success' : form.status === 'DRAFT' ? 'status-warning' : 'status-inactive'}`}>
                {form.status ? t('recipes.status.' + form.status, { defaultValue: form.status }) : form.status}
              </span>
            </div>
          )}
        </div>

        <div className="form-group">
          <label>{t('viewRecipe.fields.totalCost')}</label>
          <div className="field-value"><strong>{formatCurrency(totalCost)}</strong></div>
        </div>

        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
          <label>{t('common:ui.description')}</label>
          {isEditing ? (
            <textarea value={form.description} onChange={(e) => handleChange('description', e.target.value)} rows={2} placeholder={t('viewRecipe.placeholders.description')} />
          ) : (
            <div className="field-value">{form.description || '-'}</div>
          )}
        </div>
      </div>

      {/* Audit Info */}
      {!createMode && (form.created_by || form.updated_by) && (
        <div style={{ marginBottom: '16px', padding: '10px 16px', background: 'var(--bg-secondary)', borderRadius: '8px', fontSize: '0.85em', color: 'var(--text-secondary)', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          {form.created_by && (
            <span>{t('viewRecipe.audit.createdBy')} <strong>{form.created_by}</strong>{form.created_date ? ` ${t('viewRecipe.audit.onDate', { date: `${formatAppDate(form.created_date)} ${formatAppDate(form.created_date, 'time')}` })}` : ''}</span>
          )}
          {form.updated_by && (
            <span>{t('viewRecipe.audit.updatedBy')} <strong>{form.updated_by}</strong>{form.updated_date ? ` ${t('viewRecipe.audit.onDate', { date: `${formatAppDate(form.updated_date)} ${formatAppDate(form.updated_date, 'time')}` })}` : ''}</span>
          )}
        </div>
      )}

      {/* Items Table */}
      <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>{t('viewRecipe.items.title')}</h3>
        {isEditing && (
          <button className="btn-secondary" onClick={addItem}>+ {t('viewRecipe.items.add')}</button>
        )}
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('common:ui.material')}</th>
              <th style={{ width: '100px' }}>{t('common:ui.quantity')}</th>
              <th style={{ width: '80px' }}>{t('tableHeaders.unitOfMeasure')}</th>
              <th style={{ width: '100px' }}>{t('viewRecipe.items.unitCost')}</th>
              <th style={{ width: '110px' }}>{t('viewRecipe.items.extCost')}</th>
              {isEditing && <th style={{ width: '50px' }}></th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={isEditing ? 7 : 6} className="no-data">{t('viewRecipe.items.empty')}</td></tr>
            ) : (
              items.map((item, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>
                    {isEditing ? (
                      <div style={{ position: 'relative', minWidth: '200px' }}>
                        <input
                          type="text"
                          list={`material-list-${idx}`}
                          value={ingredientSearchTexts[idx] || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setIngredientSearchTexts(prev => ({ ...prev, [idx]: value }));
                            setItemSearchTerm(value);
                            const match = inventoryItems.find(i =>
                              `${i.item_name} (${i.item_code})` === value ||
                              i.item_code === value ||
                              i.item_name === value
                            );
                            if (match) {
                              updateItem(idx, 'inventory_item_id', match.item_id);
                            } else if (!value) {
                              updateItem(idx, 'inventory_item_id', '');
                            }
                          }}
                          placeholder={t('viewRecipe.placeholders.searchMaterials')}
                          style={{ width: '100%' }}
                        />
                        <datalist id={`material-list-${idx}`}>
                          {inventoryItems.map(inv => (
                            <option key={inv.item_id} value={`${inv.item_name} (${inv.item_code})`} />
                          ))}
                        </datalist>
                      </div>
                    ) : (
                      <span>{item.item_code} - {item.item_name}</span>
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.0001"
                        value={item.quantity || ''}
                        onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                        style={{ width: '90px' }}
                      />
                    ) : (
                      parseFloat(item.quantity || 0).toFixed(4)
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <UOMSelect
                        value={item.unit_of_measure || ''}
                        onChange={(value) => updateItem(idx, 'unit_of_measure', value)}
                        grouped={false}
                      />
                    ) : (
                      item.unit_of_measure || 'KG'
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        type="number"
                        step="0.01"
                        value={item.unit_cost || ''}
                        onChange={(e) => updateItem(idx, 'unit_cost', e.target.value)}
                        style={{ width: '90px' }}
                      />
                    ) : (
                      formatCurrency(item.unit_cost)
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <strong>{formatCurrency(calcExtendedCost(item))}</strong>
                  </td>
                  {isEditing && (
                    <td>
                      <button
                        className="btn-danger"
                        onClick={() => removeItem(idx)}
                        style={{ padding: '2px 8px', fontSize: '12px' }}
                        title={t('common:ui.remove')}
                      >
                        ✕
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
            {items.length > 0 && (
              <tr style={{ fontWeight: 'bold', background: 'var(--bg-secondary)' }}>
                <td colSpan={isEditing ? 5 : 5} style={{ textAlign: 'right' }}>{t('common:ui.total')}:</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(totalCost)}</td>
                {isEditing && <td></td>}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default ViewRecipe;
